import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { GridCellParams, GridRowSelectionModel } from '@mui/x-data-grid-pro';

import { translate as t } from 'hooks/useTranslations';
import GridSection, { ColumnDef } from 'components/_layouts/GridSection';

import type { Organization } from 'views/App/Portal/LexID/Organizations/_types/organization';

interface SelectedOrganizationsTabProps {
  selectedOrganizations: Organization[];
  toggleOrganization: (org: Organization) => void;
}

const SelectedOrganizationsTab: React.FC<SelectedOrganizationsTabProps> = ({
  selectedOrganizations,
  toggleOrganization,
}) => {
  // Get selected organization IDs for checkbox state
  const selectedOrgIds = useMemo(() => {
    return selectedOrganizations.map((org) => org.organization.id);
  }, [selectedOrganizations]);

  // Handle selection changes - when unchecked, remove from selection
  const handleSelectionChange = (selectionModel: GridRowSelectionModel) => {
    const newSelectedIds = selectionModel as string[];

    // Find organizations that were unchecked (removed from selection)
    selectedOrgIds.forEach((orgId) => {
      if (!newSelectedIds.includes(orgId)) {
        const org = selectedOrganizations.find((o) => o.organization.id === orgId);
        if (org) {
          toggleOrganization(org);
        }
      }
    });
  };

  const columns: ColumnDef = useMemo(
    () => ({
      name: {
        header: t('riskPoolSharing.properties.organizationName'),
        flex: 2,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return (
            <Box component="span" sx={{ fontWeight: 600 }}>
              {row.organization?.name}
            </Box>
          );
        },
      },
      state: {
        header: t('riskPoolSharing.properties.jurisdiction'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <Box component="span">{row.organization?.state || '-'}</Box>;
        },
      },
      city: {
        header: t('riskPoolSharing.properties.city'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <Box component="span">{row.organization?.city || '-'}</Box>;
        },
      },
      lastUpdate: {
        header: t('riskPoolSharing.properties.updated'),
        flex: 0.8,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row || !row.organization?.lastUpdate) return null;
          return (
            <Box component="span">
              {new Date(row.organization.lastUpdate).toLocaleDateString()}
            </Box>
          );
        },
      },
    }),
    [t]
  );

  const gridViewQuery = useMemo(
    () => ({
      hasNextPage: false,
      isFetchingNextPage: false,
      fetchNextPage: () => Promise.resolve(),
      isError: false,
      isLoading: false,
      error: null,
      data: {
        pages: [
          {
            count: selectedOrganizations.length,
            rows: selectedOrganizations,
          },
        ],
      },
    }),
    [selectedOrganizations]
  );

  // Empty state
  if (selectedOrganizations.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 400,
          gap: 2,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          {t('riskPoolSharing.add.emptyState.noSelected')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('riskPoolSharing.add.emptyState.selectFromAllTab')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: 0, height: '100%' }}>
      <GridSection
        columns={columns}
        data={selectedOrganizations}
        query={gridViewQuery}
        gridProps={{
          checkboxSelection: true,
          rowSelectionModel: selectedOrgIds,
          onRowSelectionModelChange: handleSelectionChange,
          getRowId: (row: Organization) => row.organization.id,
        }}
      />
    </Box>
  );
};

export default SelectedOrganizationsTab;

